import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import YouzuLensPage from "@/pages/YouzuLensPage";
import RoomVisualizerPage from "@/pages/RoomVisualizerPage";
import YouzuSwapPage from "@/pages/YouzuSwapPage";
import MagicBackgroundPage from "@/pages/MagicBackgroundPage";
import TwoDToThreeDPage from "@/pages/TwoDToThreeDPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import IntegrationHubPage from "@/pages/IntegrationHubPage";
import CatalogPage from "@/pages/CatalogPage";
import CategoryTemplatesPage from "@/pages/CategoryTemplatesPage";
import ModerationRulesPage from "@/pages/ModerationRulesPage";
import TasksPage from "@/pages/TasksPage";
import NotFoundPage from "@/pages/NotFoundPage";
import SignInPage from "@/pages/SignInPage";
import SignUpPage from "@/pages/SignUpPage";
import VerificationPendingPage from "@/pages/VerificationPendingPage";
import ResendVerificationPage from "@/pages/ResendVerificationPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AccountPage from "@/pages/AccountPage";
import OrganizationPage from "@/pages/OrganizationPage";
import AcceptInvitationPage from "@/pages/AcceptInvitationPage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireFeature } from "@/components/RequireFeature";
import { DashboardLayout } from "@/components/dashboard-layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { TasksProvider } from "@/context/TasksContext";
import type { FeatureKey } from "@/constants/permissions";

// Shorthand for feature-gated route elements
const gated = (feature: FeatureKey, element: React.ReactNode) => (
  <RequireFeature feature={feature}>{element}</RequireFeature>
);

// Layout wrapper for protected routes
const ProtectedDashboardLayout = () => {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <TasksProvider>
      <>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route
            path="/verification-pending"
            element={<VerificationPendingPage />}
          />
          <Route
            path="/resend-verification"
            element={<ResendVerificationPage />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/accept-invitation/:invitationId"
            element={<AcceptInvitationPage />}
          />
          {/* Protected Routes with DashboardLayout */}
          <Route element={<ProtectedDashboardLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/youzu-lens" element={gated("ai_tools", <YouzuLensPage />)} />
            <Route path="/room-visualizer" element={gated("ai_tools", <RoomVisualizerPage />)} />
            <Route path="/property-visualize" element={<Navigate to="/room-visualizer" replace />} />
            <Route path="/youzu-swap" element={gated("ai_tools", <YouzuSwapPage />)} />
            <Route path="/youzu-fill" element={<Navigate to="/room-visualizer" replace />} />
            <Route path="/magic-background" element={gated("ai_tools", <MagicBackgroundPage />)} />
            <Route path="/2d-to-3d" element={gated("ai_tools", <TwoDToThreeDPage />)} />
            <Route path="/analytics" element={gated("analytics", <AnalyticsPage />)} />
            <Route path="/activities" element={gated("analytics", <ActivitiesPage />)} />
            <Route path="/integration-hub" element={gated("api_keys", <IntegrationHubPage />)} />
            <Route path="/catalog" element={gated("catalog", <CatalogPage />)} />
            <Route path="/catalog/categories-attributes" element={gated("catalog", <CategoryTemplatesPage />)} />
            <Route path="/catalog/moderation-rules" element={gated("catalog", <ModerationRulesPage />)} />
            <Route path="/catalog/:catalogueId" element={gated("catalog", <CatalogPage />)} />
            <Route path="/catalog/:catalogueId/product/:productId" element={gated("catalog", <ProductDetailPage />)} />
            <Route path="/tasks" element={gated("ai_tools", <TasksPage />)} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/organization" element={<OrganizationPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Fallback redirect for any unmatched routes */}
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </>
    </TasksProvider>
  );
}

export default App;
